import * as THREE from 'three';
import { FullscreenPass, makeTarget } from './gpu';

// Frame graph:
//   1. opaque pass (layer 0, MSAA, HDR)  -> opaqueRT (+ depth)
//   2. copy opaque colour + depth        -> sceneRT
//   3. water & transparents (layer 1)    -> sceneRT, sampling opaqueRT for refraction
//   4. camera motion blur (ship masked)  -> post A
//   5. depth of field                    -> post B
//   6. bloom mip chain, god rays, auto exposure
//   7. AgX tone map + grade + grain      -> screen
//   8. overlay (world marks such as the aim), over the finished picture

export const LAYER_OPAQUE = 0;
export const LAYER_WATER = 1;

export interface PostSettings {
  bloom: boolean;
  dof: boolean;
  motionBlur: boolean;
  godRays: boolean;
  grain: boolean;
  /** 0..1 depth-of-field strength (camera mode driven). */
  dofAmount: number;
  /** Focus distance, m. */
  focus: number;
  exposureBias: number;
  /** Colour temperature shift, -1 cool .. +1 warm. */
  warmth: number;
  /** 0 day .. 1 night (Purkinje shift, lower saturation). */
  night: number;
  saturation: number;
  /** Lightning flash (bypasses adaptation briefly). */
  flash: number;
}

const COPY_FRAG = /* glsl */ `
uniform sampler2D tColor;
uniform sampler2D tDepth;
in vec2 vUv;
void main() {
  gl_FragColor = texture(tColor, vUv);
  gl_FragDepth = texture(tDepth, vUv).r;
}
`;

const MOTION_FRAG = /* glsl */ `
uniform sampler2D tColor;
uniform sampler2D tDepth;
uniform mat4 uInvViewProj;
uniform mat4 uPrevViewProj;
uniform mat4 uShipDelta;
uniform float uStrength;
in vec2 vUv;
void main() {
  vec4 c = texture(tColor, vUv);
  if (any(isnan(c)) || any(isinf(c))) c = vec4(0.0, 0.0, 0.0, 1.0);
  float d = texture(tDepth, vUv).r;
  vec4 wp = uInvViewProj * vec4(vUv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  wp /= wp.w;
  // Pixels flagged as ship (alpha 0) moved with the ship.
  if (c.a < 0.5) wp = uShipDelta * wp;
  vec4 pc = uPrevViewProj * wp;
  vec2 puv = pc.xy / pc.w * 0.5 + 0.5;
  vec2 vel = (vUv - puv) * uStrength;
  float len = length(vel);
  if (len > 0.04) vel *= 0.04 / len;
  if (len < 0.0006) { gl_FragColor = c; return; }
  vec3 acc = vec3(0.0);
  float n = 0.0;
  for (int i = 0; i < 10; i++) {
    float t = float(i) / 9.0 - 0.5;
    acc += texture(tColor, vUv + vel * t).rgb;
    n += 1.0;
  }
  gl_FragColor = vec4(acc / n, c.a);
}
`;

const COC_FRAG = /* glsl */ `
uniform sampler2D tColor;
uniform sampler2D tDepth;
uniform vec2 uNearFar;
uniform float uFocus;
uniform float uAperture;
uniform float uMaxCoc;
in vec2 vUv;
float viewZ(float d) {
  float n = uNearFar.x, f = uNearFar.y;
  return (n * f) / ((f - n) * d - f);
}
void main() {
  float z = -viewZ(texture(tDepth, vUv).r);
  float coc = uAperture * (z - uFocus) / max(z, 0.01);
  coc = clamp(coc, -uMaxCoc, uMaxCoc);
  gl_FragColor = vec4(texture(tColor, vUv).rgb, coc);
}
`;

const DOF_FRAG = /* glsl */ `
uniform sampler2D tCoc;
uniform vec2 uTexel;
in vec2 vUv;
void main() {
  vec4 c0 = texture(tCoc, vUv);
  float r0 = abs(c0.a);
  vec3 acc = c0.rgb;
  float wsum = 1.0;
  const float GA = 2.39996323;
  for (int i = 1; i < 32; i++) {
    float fi = float(i);
    float r = sqrt(fi / 32.0) * 1.0;
    vec2 o = vec2(cos(fi * GA), sin(fi * GA)) * r;
    vec4 s = texture(tCoc, vUv + o * uTexel * max(r0, 0.5));
    // Gather samples whose own blur reaches this pixel (and near-field bleed).
    float sr = abs(s.a);
    float w = smoothstep(r * r0 - 0.5, r * r0 + 0.5, sr) + (s.a < 0.0 ? 0.6 : 0.0);
    w = clamp(w, 0.0, 1.0);
    acc += s.rgb * w;
    wsum += w;
  }
  gl_FragColor = vec4(acc / wsum, c0.a);
}
`;

const DOF_COMPOSITE_FRAG = /* glsl */ `
uniform sampler2D tColor;
uniform sampler2D tBlur;
uniform sampler2D tCoc;
in vec2 vUv;
void main() {
  vec4 c = texture(tColor, vUv);
  vec4 b = texture(tBlur, vUv);
  float coc = abs(texture(tCoc, vUv).a);
  float t = smoothstep(0.6, 2.2, coc);
  gl_FragColor = vec4(mix(c.rgb, b.rgb, t), c.a);
}
`;

const DOWN_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uTexel;
uniform float uFirst;
in vec2 vUv;
vec3 s(vec2 o) { return texture(tSrc, vUv + o * uTexel).rgb; }
void main() {
  // 13-tap filter (Jimenez 2014).
  vec3 a = s(vec2(-2, 2)), b = s(vec2(0, 2)), c = s(vec2(2, 2));
  vec3 d = s(vec2(-2, 0)), e = s(vec2(0, 0)), f = s(vec2(2, 0));
  vec3 g = s(vec2(-2, -2)), h = s(vec2(0, -2)), i = s(vec2(2, -2));
  vec3 j = s(vec2(-1, 1)), k = s(vec2(1, 1)), l = s(vec2(-1, -1)), m = s(vec2(1, -1));
  vec3 o = e * 0.125 + (a + c + g + i) * 0.03125 + (b + d + f + h) * 0.0625 + (j + k + l + m) * 0.125;
  if (uFirst > 0.5) {
    if (any(isnan(o)) || any(isinf(o))) o = vec3(0.0);
    o = min(o, vec3(250.0));
  }
  gl_FragColor = vec4(o, 1.0);
}
`;

const UP_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform sampler2D tBase;
uniform vec2 uTexel;
uniform float uRadius;
in vec2 vUv;
void main() {
  vec2 t = uTexel * uRadius;
  vec3 o = texture(tSrc, vUv + vec2(-t.x, t.y)).rgb + texture(tSrc, vUv + vec2(0.0, t.y)).rgb * 2.0 + texture(tSrc, vUv + t).rgb
    + texture(tSrc, vUv + vec2(-t.x, 0.0)).rgb * 2.0 + texture(tSrc, vUv).rgb * 4.0 + texture(tSrc, vUv + vec2(t.x, 0.0)).rgb * 2.0
    + texture(tSrc, vUv - t).rgb + texture(tSrc, vUv - vec2(0.0, t.y)).rgb * 2.0 + texture(tSrc, vUv + vec2(t.x, -t.y)).rgb;
  gl_FragColor = vec4(texture(tBase, vUv).rgb + o / 16.0, 1.0);
}
`;

const GODRAY_MASK_FRAG = /* glsl */ `
uniform sampler2D tDepth;
uniform sampler2D tSky;
uniform vec2 uSunUV;
uniform float uAspect;
in vec2 vUv;
void main() {
  float d = texture(tDepth, vUv).r;
  float sky = d > 0.99999 ? texture(tSky, vUv).a : 0.0;
  vec2 dv = (vUv - uSunUV) * vec2(uAspect, 1.0);
  float glow = exp(-dot(dv, dv) * 18.0);
  gl_FragColor = vec4(vec3(sky * glow), 1.0);
}
`;

const GODRAY_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uSunUV;
uniform float uStep;
in vec2 vUv;
void main() {
  vec2 dir = (uSunUV - vUv) * uStep;
  vec2 uv = vUv;
  float decay = 1.0;
  vec3 acc = vec3(0.0);
  for (int i = 0; i < 40; i++) {
    acc += texture(tSrc, uv).rgb * decay;
    uv += dir;
    decay *= 0.965;
  }
  gl_FragColor = vec4(acc / 16.0, 1.0);
}
`;

const LUM_FRAG = /* glsl */ `
uniform sampler2D tColor;
in vec2 vUv;
void main() {
  vec3 c = texture(tColor, vUv).rgb;
  if (any(isnan(c)) || any(isinf(c))) c = vec3(0.0);
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  // Centre-weighted metering.
  vec2 d = vUv - 0.5;
  float w = 1.0 - dot(d, d) * 1.6;
  gl_FragColor = vec4(log(max(l, 1e-5)) * w, w, 0.0, 1.0);
}
`;

const ADAPT_FRAG = /* glsl */ `
uniform sampler2D tLum;
uniform sampler2D tPrev;
uniform float uDt;
uniform float uMinExp;
uniform float uMaxExp;
uniform float uKey;
uniform float uReset;
in vec2 vUv;
void main() {
  vec2 m = textureLod(tLum, vec2(0.5), 20.0).rg;
  float avg = exp(m.x / max(m.y, 1e-3));
  float target = clamp(uKey / max(avg, 1e-5), uMinExp, uMaxExp);
  float prev = texture(tPrev, vec2(0.5)).r;
  if (isnan(prev) || isinf(prev) || prev <= 0.0) prev = target;
  float rate = target > prev ? 1.1 : 2.2;
  float e = uReset > 0.5 ? target : mix(prev, target, 1.0 - exp(-uDt * rate));
  gl_FragColor = vec4(e, 0.0, 0.0, 1.0);
}
`;

const FINAL_FRAG = /* glsl */ `
uniform sampler2D tColor;
uniform sampler2D tBloom;
uniform sampler2D tRays;
uniform sampler2D tExposure;
uniform float uBloom;
uniform vec3 uRayColor;
uniform float uBias;
uniform float uWarmth;
uniform float uNight;
uniform float uSat;
uniform float uGrain;
uniform float uTime;
uniform float uFlash;
uniform vec2 uRes;
in vec2 vUv;

const mat3 SRGB_TO_2020 = mat3(vec3(0.6274, 0.0691, 0.0164), vec3(0.3293, 0.9195, 0.0880), vec3(0.0433, 0.0113, 0.8956));
const mat3 R2020_TO_SRGB = mat3(vec3(1.6605, -0.1246, -0.0182), vec3(-0.5876, 1.1329, -0.1006), vec3(-0.0728, -0.0083, 1.1187));
const mat3 AGX_IN = mat3(vec3(0.856627153315983, 0.137318972929847, 0.11189821299995), vec3(0.0951212405381588, 0.761241990602591, 0.0767994186031903), vec3(0.0482516061458583, 0.101439036467562, 0.811302368396859));
const mat3 AGX_OUT = mat3(vec3(1.1271005818144368, -0.1413297634984383, -0.14132976349843826), vec3(-0.11060664309660323, 1.157823702216272, -0.11060664309660294), vec3(-0.016493938717834573, -0.016493938717834257, 1.2519364065950405));

vec3 agxContrast(vec3 x) {
  vec3 x2 = x * x, x4 = x2 * x2;
  return 15.5 * x4 * x2 - 40.14 * x4 * x + 31.96 * x4 - 6.868 * x2 * x + 0.4298 * x2 + 0.1191 * x - 0.00232;
}
vec3 agx(vec3 c) {
  c = AGX_IN * (SRGB_TO_2020 * c);
  c = clamp((log2(max(c, 1e-10)) + 12.47393) / 16.5, 0.0, 1.0);
  c = agxContrast(c);
  // "Punchy"-leaning look: a touch of contrast, keep saturation natural.
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = l + (c - l) * uSat;
  c = pow(max(c, 0.0), vec3(1.06));
  c = AGX_OUT * c;
  c = pow(max(c, 0.0), vec3(2.2));
  return clamp(R2020_TO_SRGB * c, 0.0, 1.0);
}
vec3 toSRGB(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}
void main() {
  vec2 d = vUv - 0.5;
  // Very slight lateral chromatic aberration toward the edges.
  float ca = dot(d, d) * 0.004;
  vec3 col;
  col.r = texture(tColor, vUv - d * ca).r;
  col.g = texture(tColor, vUv).g;
  col.b = texture(tColor, vUv + d * ca).b;
  vec3 bloom = texture(tBloom, vUv).rgb;
  if (any(isnan(col)) || any(isinf(col))) col = vec3(0.0);
  if (any(isnan(bloom)) || any(isinf(bloom))) bloom = vec3(0.0);
  col = mix(col, bloom, uBloom);
  col += texture(tRays, vUv).rgb * uRayColor;
  float exposure = texture(tExposure, vec2(0.5)).r * exp2(uBias);
  col *= exposure * (1.0 + uFlash * 1.5);
  // White balance.
  col *= vec3(1.0 + 0.06 * uWarmth, 1.0, 1.0 - 0.07 * uWarmth);
  // Scotopic shift at night: desaturate toward a blue-grey.
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(col, lum * vec3(0.72, 0.86, 1.12), uNight * 0.6);
  col = agx(col);
  // Vignette.
  float v = (1.0 - smoothstep(0.3, 0.95, length(d * vec2(uRes.x / uRes.y, 1.0) * 0.82)));
  col *= mix(0.72, 1.0, v);
  col = toSRGB(col);
  // Film grain in display space, strongest in the mid-tones and shadows.
  float dl = clamp(dot(col, vec3(0.2126, 0.7152, 0.0722)), 0.0, 1.0);
  float g = hash(vUv * uRes + fract(uTime * 13.37) * 100.0) - 0.5;
  col = clamp(col + g * uGrain * (1.0 - dl * 0.7), 0.0, 1.0);
  gl_FragColor = vec4(col, 1.0);
}
`;

export class Pipeline {
  private width = 1;
  private height = 1;
  scale = 1;
  private opaqueRT: THREE.WebGLRenderTarget;
  private sceneRT: THREE.WebGLRenderTarget;
  private postA: THREE.WebGLRenderTarget;
  private postB: THREE.WebGLRenderTarget;
  private cocRT: THREE.WebGLRenderTarget;
  private dofRT: THREE.WebGLRenderTarget;
  private bloomMips: THREE.WebGLRenderTarget[] = [];
  private bloomUp: THREE.WebGLRenderTarget[] = [];
  private rayA: THREE.WebGLRenderTarget;
  private rayB: THREE.WebGLRenderTarget;
  private lumRT: THREE.WebGLRenderTarget;
  private adapt: THREE.WebGLRenderTarget[];
  private adaptIndex = 0;

  private readonly copyScene = new THREE.Scene();
  private readonly copyMat: THREE.ShaderMaterial;
  private readonly motion = new FullscreenPass(MOTION_FRAG, {
    tColor: { value: null }, tDepth: { value: null }, uInvViewProj: { value: new THREE.Matrix4() },
    uPrevViewProj: { value: new THREE.Matrix4() }, uShipDelta: { value: new THREE.Matrix4() }, uStrength: { value: 0.5 },
  });
  private readonly coc = new FullscreenPass(COC_FRAG, {
    tColor: { value: null }, tDepth: { value: null }, uNearFar: { value: new THREE.Vector2() }, uFocus: { value: 30 },
    uAperture: { value: 2 }, uMaxCoc: { value: 8 },
  });
  private readonly dof = new FullscreenPass(DOF_FRAG, { tCoc: { value: null }, uTexel: { value: new THREE.Vector2() } });
  private readonly dofComp = new FullscreenPass(DOF_COMPOSITE_FRAG, { tColor: { value: null }, tBlur: { value: null }, tCoc: { value: null } });
  private readonly down = new FullscreenPass(DOWN_FRAG, { tSrc: { value: null }, uTexel: { value: new THREE.Vector2() }, uFirst: { value: 0 } });
  private readonly up = new FullscreenPass(UP_FRAG, { tSrc: { value: null }, tBase: { value: null }, uTexel: { value: new THREE.Vector2() }, uRadius: { value: 1 } });
  private readonly rayMask = new FullscreenPass(GODRAY_MASK_FRAG, { tDepth: { value: null }, tSky: { value: null }, uSunUV: { value: new THREE.Vector2() }, uAspect: { value: 1 } });
  private readonly rays = new FullscreenPass(GODRAY_FRAG, { tSrc: { value: null }, uSunUV: { value: new THREE.Vector2() }, uStep: { value: 0.02 } });
  private readonly lum = new FullscreenPass(LUM_FRAG, { tColor: { value: null } });
  private readonly adaptPass = new FullscreenPass(ADAPT_FRAG, {
    tLum: { value: null }, tPrev: { value: null }, uDt: { value: 0 }, uMinExp: { value: 0.05 }, uMaxExp: { value: 6 }, uKey: { value: 0.16 }, uReset: { value: 1 },
  });
  private readonly final = new FullscreenPass(FINAL_FRAG, {
    tColor: { value: null }, tBloom: { value: null }, tRays: { value: null }, tExposure: { value: null }, uBloom: { value: 0.04 },
    uRayColor: { value: new THREE.Color() }, uBias: { value: 0 }, uWarmth: { value: 0 }, uNight: { value: 0 }, uSat: { value: 1 },
    uGrain: { value: 0.02 }, uTime: { value: 0 }, uFlash: { value: 0 }, uRes: { value: new THREE.Vector2() },
  });

  private readonly prevViewProj = new THREE.Matrix4();
  private readonly prevShip = new THREE.Matrix4();
  private hasPrev = false;
  private exposureReset = true;
  readonly settings: PostSettings = {
    bloom: true, dof: true, motionBlur: true, godRays: true, grain: true, dofAmount: 0.3, focus: 40,
    exposureBias: 0, warmth: 0, night: 0, saturation: 1, flash: 0,
  };
  msaa = 4;
  /**
   * Marks in the world drawn over the finished picture with the camera (the
   * aim): sharp, the same brightness by day or night, untouched by blur and
   * bloom. They see the scene through `sceneDepth` for what hides them.
   */
  readonly overlay = new THREE.Scene();

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    this.opaqueRT = this.makeSceneTarget();
    this.sceneRT = this.makeSceneTarget();
    this.postA = makeTarget(4, 4);
    this.postB = makeTarget(4, 4);
    this.cocRT = makeTarget(4, 4);
    this.dofRT = makeTarget(4, 4);
    this.rayA = makeTarget(4, 4);
    this.rayB = makeTarget(4, 4);
    this.lumRT = makeTarget(256, 128, { minFilter: THREE.LinearMipmapLinearFilter, generateMipmaps: true });
    this.adapt = [0, 1].map(() => makeTarget(1, 1, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter }));
    for (let i = 0; i < 6; i++) {
      this.bloomMips.push(makeTarget(4, 4));
      this.bloomUp.push(makeTarget(4, 4));
    }
    this.copyMat = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: COPY_FRAG,
      uniforms: { tColor: { value: null }, tDepth: { value: null } },
      depthTest: true,
      depthWrite: true,
      depthFunc: THREE.AlwaysDepth,
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    const m = new THREE.Mesh(g, this.copyMat);
    m.frustumCulled = false;
    this.copyScene.add(m);
  }

  private makeSceneTarget(): THREE.WebGLRenderTarget {
    const rt = new THREE.WebGLRenderTarget(4, 4, {
      type: THREE.HalfFloatType,
      samples: 4,
      depthBuffer: true,
      stencilBuffer: false,
    });
    rt.depthTexture = new THREE.DepthTexture(4, 4, THREE.FloatType);
    rt.depthTexture.format = THREE.DepthFormat;
    return rt;
  }

  get opaqueColor(): THREE.Texture {
    return this.opaqueRT.texture;
  }
  get opaqueDepth(): THREE.Texture {
    return this.opaqueRT.depthTexture!;
  }
  /** Depth of everything drawn, the sea included (resolved after the water pass). */
  get sceneDepth(): THREE.Texture {
    return this.sceneRT.depthTexture!;
  }
  /** The canvas in device pixels (the overlay's gl_FragCoord frame). */
  get screenWidth(): number {
    return this.width;
  }
  get screenHeight(): number {
    return this.height;
  }
  get renderWidth(): number {
    return Math.max(1, Math.round(this.width * this.scale));
  }
  get renderHeight(): number {
    return Math.max(1, Math.round(this.height * this.scale));
  }

  setSize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    this.resizeTargets();
  }

  setScale(s: number): void {
    if (Math.abs(s - this.scale) < 0.01) return;
    this.scale = s;
    this.resizeTargets();
  }

  private resizeTargets(): void {
    const w = this.renderWidth, h = this.renderHeight;
    this.opaqueRT.setSize(w, h);
    this.sceneRT.setSize(w, h);
    this.opaqueRT.samples = this.msaa;
    this.sceneRT.samples = this.msaa;
    this.postA.setSize(w, h);
    this.postB.setSize(w, h);
    const hw = Math.max(1, w >> 1), hh = Math.max(1, h >> 1);
    this.cocRT.setSize(hw, hh);
    this.dofRT.setSize(hw, hh);
    let bw = hw, bh = hh;
    for (let i = 0; i < this.bloomMips.length; i++) {
      this.bloomMips[i]!.setSize(Math.max(1, bw), Math.max(1, bh));
      this.bloomUp[i]!.setSize(Math.max(1, bw), Math.max(1, bh));
      bw >>= 1;
      bh >>= 1;
    }
    this.rayA.setSize(Math.max(1, w >> 2), Math.max(1, h >> 2));
    this.rayB.setSize(Math.max(1, w >> 2), Math.max(1, h >> 2));
    this.final.uniforms.uRes!.value.set(this.width, this.height);
    this.hasPrev = false;
  }

  /** A camera cut: don't blur between the last frame and this one. */
  cut(): void {
    this.hasPrev = false;
  }

  resetExposure(): void {
    this.exposureReset = true;
  }

  render(scene: THREE.Scene, camera: THREE.PerspectiveCamera, dt: number, time: number, sky: { skyTexture: THREE.Texture | null; sunDir: THREE.Vector3; sunColor: THREE.Color; haze: number }, shipMatrix: THREE.Matrix4, night: number): void {
    const r = this.renderer;
    const s = this.settings;
    const w = this.renderWidth, h = this.renderHeight;

    // 1. Opaque world.
    r.shadowMap.needsUpdate = true;
    camera.layers.set(LAYER_OPAQUE);
    r.setRenderTarget(this.opaqueRT);
    r.setClearColor(0x000000, 1);
    r.clear(true, true, false);
    r.render(scene, camera);

    // 2. Copy colour + depth into the scene target.
    this.copyMat.uniforms.tColor!.value = this.opaqueRT.texture;
    this.copyMat.uniforms.tDepth!.value = this.opaqueRT.depthTexture;
    r.setRenderTarget(this.sceneRT);
    r.clear(true, true, false);
    r.render(this.copyScene, camera);

    // 3. Water and transparent effects.
    camera.layers.set(LAYER_WATER);
    const autoClear = r.autoClear;
    r.autoClear = false;
    r.render(scene, camera);
    r.autoClear = autoClear;
    camera.layers.set(LAYER_OPAQUE);

    let color: THREE.Texture = this.sceneRT.texture;
    const depth = this.sceneRT.depthTexture!;

    // 4. Camera motion blur.
    const viewProj = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    if (s.motionBlur && this.hasPrev) {
      const u = this.motion.uniforms;
      u.tColor!.value = color;
      u.tDepth!.value = depth;
      u.uInvViewProj!.value.copy(viewProj).invert();
      u.uPrevViewProj!.value.copy(this.prevViewProj);
      // Ship pixels: current world -> ship local -> previous world.
      u.uShipDelta!.value.copy(this.prevShip).multiply(new THREE.Matrix4().copy(shipMatrix).invert());
      // Scale to a ~1/60 s shutter regardless of frame time.
      u.uStrength!.value = THREE.MathUtils.clamp((1 / 60) / Math.max(dt, 1e-3), 0.2, 1.5) * 0.55;
      this.motion.render(r, this.postA);
      color = this.postA.texture;
    }
    this.prevViewProj.copy(viewProj);
    this.prevShip.copy(shipMatrix);
    this.hasPrev = true;

    // 5. Depth of field.
    if (s.dof && s.dofAmount > 0.01) {
      const cu = this.coc.uniforms;
      cu.tColor!.value = color;
      cu.tDepth!.value = depth;
      cu.uNearFar!.value.set(camera.near, camera.far);
      cu.uFocus!.value = s.focus;
      const hw = this.cocRT.width;
      cu.uAperture!.value = s.dofAmount * hw / 160;
      cu.uMaxCoc!.value = 3 + s.dofAmount * 9;
      this.coc.render(r, this.cocRT);
      const du = this.dof.uniforms;
      du.tCoc!.value = this.cocRT.texture;
      du.uTexel!.value.set(1 / this.cocRT.width, 1 / this.cocRT.height);
      this.dof.render(r, this.dofRT);
      const dc = this.dofComp.uniforms;
      dc.tColor!.value = color;
      dc.tBlur!.value = this.dofRT.texture;
      dc.tCoc!.value = this.cocRT.texture;
      const out = color === this.postA.texture ? this.postB : this.postA;
      this.dofComp.render(r, out);
      color = out.texture;
    }

    // 6a. Bloom.
    let bloomTex: THREE.Texture | null = null;
    if (s.bloom) {
      let src: THREE.Texture = color;
      let sw = w, sh = h;
      for (let i = 0; i < this.bloomMips.length; i++) {
        const du = this.down.uniforms;
        du.tSrc!.value = src;
        du.uTexel!.value.set(1 / sw, 1 / sh);
        du.uFirst!.value = i === 0 ? 1 : 0;
        this.down.render(r, this.bloomMips[i]!);
        src = this.bloomMips[i]!.texture;
        sw = this.bloomMips[i]!.width;
        sh = this.bloomMips[i]!.height;
      }
      let upSrc = this.bloomMips[this.bloomMips.length - 1]!.texture;
      for (let i = this.bloomMips.length - 2; i >= 0; i--) {
        const uu = this.up.uniforms;
        uu.tSrc!.value = upSrc;
        uu.tBase!.value = this.bloomMips[i]!.texture;
        uu.uTexel!.value.set(1 / this.bloomMips[i + 1]!.width, 1 / this.bloomMips[i + 1]!.height);
        this.up.render(r, this.bloomUp[i]!);
        upSrc = this.bloomUp[i]!.texture;
      }
      bloomTex = upSrc;
    }

    // 6b. God rays from the sun through gaps in cloud and rigging.
    const sunClip = new THREE.Vector4(sky.sunDir.x, sky.sunDir.y, sky.sunDir.z, 0).applyMatrix4(viewProj);
    let rayStrength = 0;
    if (s.godRays && sky.skyTexture && sunClip.w > 0 && sky.sunDir.y > -0.02) {
      const sx = (sunClip.x / sunClip.w) * 0.5 + 0.5;
      const sy = (sunClip.y / sunClip.w) * 0.5 + 0.5;
      const off = Math.max(Math.abs(sx - 0.5), Math.abs(sy - 0.5));
      rayStrength = 1 - THREE.MathUtils.smoothstep(off, 0.4, 1.2);
      if (rayStrength > 0.001) {
        const mu = this.rayMask.uniforms;
        mu.tDepth!.value = depth;
        mu.tSky!.value = sky.skyTexture;
        mu.uSunUV!.value.set(sx, sy);
        mu.uAspect!.value = w / h;
        this.rayMask.render(r, this.rayA);
        const ru = this.rays.uniforms;
        ru.uSunUV!.value.set(sx, sy);
        ru.tSrc!.value = this.rayA.texture;
        ru.uStep!.value = 0.022;
        this.rays.render(r, this.rayB);
        ru.tSrc!.value = this.rayB.texture;
        ru.uStep!.value = 0.009;
        this.rays.render(r, this.rayA);
      }
    }

    // 6c. Auto exposure.
    this.lum.uniforms.tColor!.value = color;
    this.lum.render(r, this.lumRT);
    const prev = this.adapt[this.adaptIndex]!;
    this.adaptIndex ^= 1;
    const au = this.adaptPass.uniforms;
    au.tLum!.value = this.lumRT.texture;
    au.tPrev!.value = prev.texture;
    au.uDt!.value = dt;
    au.uReset!.value = this.exposureReset ? 1 : 0;
    // Let night stay night: cap how far the eye adapts.
    au.uMaxExp!.value = THREE.MathUtils.lerp(3.2, 24, night);
    au.uKey!.value = THREE.MathUtils.lerp(0.17, 0.09, night);
    this.adaptPass.render(r, this.adapt[this.adaptIndex]!);
    this.exposureReset = false;

    // 7. Final.
    const fu = this.final.uniforms;
    fu.tColor!.value = color;
    fu.tBloom!.value = bloomTex ?? color;
    fu.uBloom!.value = bloomTex ? 0.045 : 0;
    fu.tRays!.value = rayStrength > 0.001 ? this.rayA.texture : this.rayB.texture;
    const rc = fu.uRayColor!.value as THREE.Color;
    rc.copy(sky.sunColor).multiplyScalar(rayStrength * (0.035 + sky.haze * 0.08));
    if (rayStrength <= 0.001) rc.setRGB(0, 0, 0);
    fu.tExposure!.value = this.adapt[this.adaptIndex]!.texture;
    fu.uBias!.value = s.exposureBias;
    fu.uWarmth!.value = s.warmth;
    fu.uNight!.value = s.night;
    fu.uSat!.value = s.saturation;
    fu.uGrain!.value = s.grain ? 0.018 : 0;
    fu.uTime!.value = time;
    fu.uFlash!.value = s.flash;
    this.final.render(r, null);

    // 8. Overlay.
    if (this.overlay.children.some((o) => o.visible)) {
      const ac = r.autoClear;
      r.autoClear = false;
      r.render(this.overlay, camera);
      r.autoClear = ac;
    }
  }
}
